import React from "react";
import {
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  Box,
  SxProps,
  Theme,
} from "@mui/material";
import { colors } from "./colors";

export interface ModalProps extends MuiDialogProps {
  children: React.ReactNode;
  /** When 'compact', modal uses auto height and smaller default size for dialogs */
  variant?: 'default' | 'compact';
}

// Design system modal tokens
const MODAL_TOKENS = {
  backgroundColor: colors.modal.background, // #1A1F2D
  overlay: colors.modal.overlay,
  borderRadius: "20px",
  borderRadiusMobile: "20px",
  boxShadow: `0px 0px 70px ${colors.modal.boxShadowColor}`,
  mobileBreakpoint: "768px",
  defaultWidth: "758px",
  defaultHeight: "643px",
} as const;

const Modal: React.FC<ModalProps> = ({ children, PaperProps, variant = 'default', ...props }) => {
  const isCompact = variant === 'compact';
  // Paper styles - transparent to allow inner Box background
  const paperSx: SxProps<Theme> = {
    backgroundColor: "transparent",
    color: colors.neutral.white,
    borderRadius: isCompact ? "16px" : MODAL_TOKENS.borderRadius,
    width: isCompact ? "90%" : MODAL_TOKENS.defaultWidth,
    maxWidth: isCompact ? "480px" : MODAL_TOKENS.defaultWidth,
    height: isCompact ? "auto" : MODAL_TOKENS.defaultHeight,
    maxHeight: isCompact ? "90vh" : MODAL_TOKENS.defaultHeight,
    minHeight: isCompact ? 0 : undefined,
    boxShadow: MODAL_TOKENS.boxShadow,
    [`@media (max-width: ${MODAL_TOKENS.mobileBreakpoint})`]: {
      width: "90%",
      maxWidth: "90%",
      height: "auto",
      maxHeight: "90vh",
      borderRadius: isCompact ? "12px" : MODAL_TOKENS.borderRadiusMobile,
    },
    ...PaperProps?.sx,
  };

  // Inner container styles (ensures background color is applied)
  const containerSx: SxProps<Theme> = {
    background: colors.modal.backgroundGradient ?? MODAL_TOKENS.backgroundColor,
    borderRadius: isCompact ? "16px" : MODAL_TOKENS.borderRadius,
    width: "100%",
    height: "100%",
    minHeight: isCompact ? "auto" : MODAL_TOKENS.defaultHeight,
    display: "flex",
    flexDirection: "column",
    gap: isCompact ? "24px" : "32px",
    position: "relative",
    overflow: "hidden",
    margin: 0,
    padding: 0,
  };

  const backdropSx: SxProps<Theme> = {
    backgroundColor: MODAL_TOKENS.overlay,
    backdropFilter: "blur(4px)",
  };

  return (
    <MuiDialog
      {...props}
      PaperProps={{
        ...PaperProps,
        sx: paperSx,
      }}
      BackdropProps={{
        sx: backdropSx,
      }}
    >
      <Box sx={containerSx}>{children}</Box>
    </MuiDialog>
  );
};

export default Modal;
